// @dl/ui — persistence around LLui's light / dark / system theme switch.
//
// Writes `data-theme` on `documentElement` and persists the user's
// choice in localStorage. When the user has not chosen (mode = system),
// the absence of `data-theme` lets LLui's `theme-dark.css`
// `prefers-color-scheme` rule take over.
//
// SSR-safe: every DOM/Storage access is guarded so importing this
// module in a Worker or test harness with no `document` doesn't throw.
// happy-dom tests still work because the guards no-op rather than
// fail.

import { themeSwitch, type ResolvedTheme, type Theme } from '@llui/components/theme-switch';

export type ThemeMode = Theme;

const STORAGE_KEY = 'dl:theme';

function readStored(): ThemeMode | null {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
    return null;
  } catch {
    return null;
  }
}

function writeStored(mode: ThemeMode): void {
  try {
    if (typeof localStorage === 'undefined') return;
    if (mode === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // ignore — quota / private mode
  }
}

/**
 * Apply the currently-persisted theme (or "system" if none).
 *
 * Call this once at app boot, BEFORE the first paint, so the page
 * never flashes the wrong palette. The right place is in `main.ts`
 * synchronously after the stylesheet is imported.
 */
export function initTheme(): void {
  themeSwitch.applyTheme(getTheme());
}

/**
 * Read the user's chosen mode. Returns "system" when no explicit
 * choice has been persisted.
 */
export function getTheme(): ThemeMode {
  return readStored() ?? 'system';
}

/**
 * Set the user's preferred mode. Persists to localStorage so the
 * choice survives a reload.
 */
export function setTheme(mode: ThemeMode): void {
  writeStored(mode);
  themeSwitch.applyTheme(mode);
}

/** Return the next value in the light → dark → system cycle. */
export function nextThemeMode(mode: ThemeMode): ThemeMode {
  return themeSwitch.update(themeSwitch.init(mode), { type: 'toggle' })[0].theme;
}

/**
 * Cycle through light → dark → system on each call. Used by the
 * theme-toggle button atom (`@dl/ui/atoms/theme-toggle`).
 */
export function cycleTheme(): ThemeMode {
  const next = nextThemeMode(getTheme());
  setTheme(next);
  return next;
}

/**
 * Resolve to the *effective* palette right now: "light" or "dark".
 *
 * If the user chose system, we read `matchMedia(prefers-color-scheme)`.
 * Always returns "light" in environments without `matchMedia` (older
 * jsdom-style runtimes) so callers don't have to defend.
 */
export function resolveEffectiveTheme(): ResolvedTheme {
  return themeSwitch.resolveTheme(getTheme());
}

/**
 * Subscribe to changes in the effective theme (driven by the user
 * toggling or, when mode = system, by the OS changing). Returns an
 * unsubscribe function.
 *
 * Implementation note: storage events fire across tabs but not in the
 * same tab (the spec calls this "the originating tab does not see its
 * own storage event"), so this listener is paired with a synchronous
 * fan-out hook inside `setTheme` would be required for in-tab updates.
 * We don't ship that hook yet because the only in-tab consumer is the
 * theme-toggle button itself, which already knows when it just
 * cycled. Add a real eventbus when a second consumer appears.
 */
// Returned by `subscribeSystemTheme` in environments without
// matchMedia (legacy jsdom etc.) — caller calls this to unsubscribe
// and it is a no-op. Pulled out so eslint's no-empty-function rule
// doesn't fire on the inline arrow.
const NOOP_UNSUBSCRIBE = (): void => undefined;

export function subscribeSystemTheme(handler: (effective: 'light' | 'dark') => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return NOOP_UNSUBSCRIBE;
  }
  return themeSwitch.watchSystemTheme((effective) => {
    if (getTheme() === 'system') handler(effective);
  });
}
